/**
 * PupRing Custom JavaScript - Cart Drawer Design Module
 * =====================================================
 * Presentation sync for the redesigned cart drawer (Claude Design, September 2026).
 * Liquid renders every cart-dependent state from the accepted cart; this module only:
 * - remembers the rewards card minimize preference and auto-expands for a beat on unlock
 * - animates newly unlocked rewards, newly free lines and the savings row once per change
 * - marks the footer shipping value when it reads FREE
 * - opens the free-item picker and save-cart sheets inside the drawer (focus, Escape, veil)
 * - orders the picker like the bundle routing: same product type first, then nearest price
 * - fires the Klaviyo "Saved Cart" event with a property-preserving checkout link
 *
 * Dependencies: cart.js (getRenderedCartState, isCartRefreshResultCurrent, formatShopMoney),
 * custom-upsells.js (UPSELL_VARIANT_IDS, isPupRingGiftCardItem), bootstrap (external)
 */

(() => {
  'use strict';

  const COLLAPSED_KEY = 'pupring-cart-rewards-collapsed';
  const OPENED_KEY = 'pupring-cart-drawer-opened-at';
  const AUTO_EXPAND_MS = 2400;
  const SAVE_MIN_AGE_MS = 5000;
  const SAVE_DWELL_MS = 45000;
  const ROUTE_DELAY_MS = 350;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const storage = {
    get(area, key) {
      try { return window[area].getItem(key); } catch (error) { return null; }
    },
    set(area, key, value) {
      try { window[area].setItem(key, value); } catch (error) { /* Private mode or blocked storage. */ }
    }
  };

  const fill = (template, values) => String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match);

  const isBogoAllocation = (allocation) => {
    const title = String(allocation?.discount_application?.title || '').toLowerCase();
    return allocation?.amount > 0 && (title.includes('buy one get one') || title.includes('bogo'));
  };

  class CartDrawerDesign {
    static getInstance() {
      if (!window._cartDrawerDesign) window._cartDrawerDesign = new CartDrawerDesign();
      return window._cartDrawerDesign;
    }

    constructor() {
      this.drawer = null;
      this.seen = null;
      this.sheet = null;
      this.opener = null;
      this.autoExpandTimer = null;
      this.routeTimer = null;
      this.saveTimers = [];
      this.rewardUnlockedAt = 0;
    }

    init() {
      const drawer = document.getElementById('offcanvas-cart');
      if (!drawer || !drawer.hasAttribute('data-cart-drawer')) return;
      if (drawer.dataset.cartDrawerDesign === 'ready') return;
      drawer.dataset.cartDrawerDesign = 'ready';
      this.drawer = drawer;
      this.bindEvents();
      this.sync(window.getRenderedCartState?.(), { initial: true });
    }

    bindEvents() {
      const drawer = this.drawer;
      drawer.addEventListener('click', (event) => this.onClick(event));
      drawer.addEventListener('keydown', (event) => this.onKeydown(event), true);
      drawer.addEventListener('submit', (event) => this.onSubmit(event));
      drawer.addEventListener('shown.bs.offcanvas', () => {
        if (!storage.get('sessionStorage', OPENED_KEY)) storage.set('sessionStorage', OPENED_KEY, String(Date.now()));
        this.applyMinimized();
        this.markShipping();
        this.scheduleSavePrompt();
      });
      drawer.addEventListener('hidden.bs.offcanvas', () => {
        this.closeSheet({ restoreFocus: false });
        this.cancelRoute();
      });
      window.addEventListener('updated.ecomify.cart', (event) => {
        const state = event?.detail?.renderedCartState || window.getRenderedCartState?.();
        if (!window.isCartRefreshResultCurrent?.(state)) return;
        this.sync(state);
      });
    }

    /* ------------------------------------------------------------
       Accepted-cart sync: presentation only, no business logic
       ------------------------------------------------------------ */
    sync(state, options = {}) {
      const cart = state?.cart;
      const card = this.drawer.querySelector('[data-cart-rewards]');
      const snapshot = this.readSnapshot(card);
      const previous = this.seen;
      this.seen = snapshot;
      this.applyMinimized();
      this.markShipping();
      if (this.sheet) this.setBackgroundInert(true);
      this.syncPicker(cart);
      this.syncSave(cart);
      if (previous && !options.initial) {
        const unlocked = [...snapshot.unlocked].filter((key) => !previous.unlocked.has(key));
        if (unlocked.length) {
          this.rewardUnlockedAt = Date.now();
          unlocked.forEach((key) => card?.querySelector(`[data-reward="${key}"]`)?.classList.add('is-new'));
          this.autoExpand(card);
        }
        snapshot.freeKeys.forEach((key) => {
          if (previous.freeKeys.has(key)) return;
          this.drawer.querySelectorAll('[data-cart-line-item][data-line-free]').forEach((line) => {
            if (line.dataset.lineKey === key) line.classList.add('is-new');
          });
        });
        if (snapshot.savings && !previous.savings) this.drawer.querySelector('.cart-totals-row--savings')?.classList.add('is-new');
      }
      this.updateSavePrompt();
    }

    readSnapshot(card) {
      const unlocked = new Set();
      card?.querySelectorAll('[data-reward][data-reward-state="unlocked"]').forEach((row) => unlocked.add(row.dataset.reward));
      const freeKeys = new Set();
      this.drawer.querySelectorAll('[data-cart-line-item][data-line-free]').forEach((line) => freeKeys.add(line.dataset.lineKey));
      return { unlocked, freeKeys, savings: Boolean(this.drawer.querySelector('#drawer-savings')) };
    }

    markShipping() {
      this.drawer.querySelectorAll('.cart-shipping-text [data-cart-shipping-calculator]').forEach((status) => {
        const unlockedText = (status.dataset.shippingUnlockedText || '').trim();
        status.classList.toggle('is-unlocked', !status.hidden && unlockedText !== '' && status.textContent.trim() === unlockedText);
      });
    }

    /* ------------------------------------------------------------
       Rewards card minimize / expand
       ------------------------------------------------------------ */
    readPreference() {
      const value = storage.get('localStorage', COLLAPSED_KEY);
      return value === '1' ? 'collapsed' : value === '0' ? 'expanded' : null;
    }

    applyMinimized() {
      const card = this.drawer.querySelector('[data-cart-rewards]');
      if (!card) return;
      const preference = this.readPreference();
      const collapsed = preference ? preference === 'collapsed' : card.dataset.rewardsCollapsedDefault === 'true';
      this.setCollapsed(card, collapsed, { persist: false });
    }

    setCollapsed(card, collapsed, { persist, animate = false }) {
      const min = card.querySelector('[data-rewards-min]');
      const full = card.querySelector('[data-rewards-full]');
      if (!min || !full) return;
      const changed = min.hidden !== !collapsed || full.hidden !== collapsed;
      min.hidden = !collapsed;
      full.hidden = collapsed;
      // Fade only on a real toggle; server refreshes must not replay the motion.
      min.classList.toggle('is-toggling', changed && collapsed && animate);
      full.classList.toggle('is-toggling', changed && !collapsed && animate);
      card.querySelectorAll('[data-rewards-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', String(!collapsed)));
      if (persist) storage.set('localStorage', COLLAPSED_KEY, collapsed ? '1' : '0');
    }

    toggleRewards(card, toggle) {
      clearTimeout(this.autoExpandTimer);
      const collapsed = !card.querySelector('[data-rewards-full]')?.hidden;
      this.setCollapsed(card, collapsed, { persist: true, animate: true });
      const target = collapsed ? card.querySelector('[data-rewards-min] [data-rewards-toggle]') : card.querySelector('[data-rewards-full] [data-rewards-toggle]');
      (target || toggle).focus({ preventScroll: true });
    }

    // A reward unlocked while the card was minimized: show it for one beat, then respect the preference.
    autoExpand(card) {
      if (!card || !card.querySelector('[data-rewards-full]')?.hidden) return;
      this.setCollapsed(card, false, { persist: false, animate: true });
      clearTimeout(this.autoExpandTimer);
      this.autoExpandTimer = setTimeout(() => {
        const current = this.drawer.querySelector('[data-cart-rewards]');
        if (current && this.readPreference() === 'collapsed') this.setCollapsed(current, true, { persist: false });
      }, AUTO_EXPAND_MS);
    }

    /* ------------------------------------------------------------
       Delegated events (survive body/footer replacement)
       ------------------------------------------------------------ */
    onClick(event) {
      const target = event.target;
      const toggle = target.closest('[data-rewards-toggle]');
      if (toggle) {
        const card = toggle.closest('[data-cart-rewards]');
        if (card) this.toggleRewards(card, toggle);
        return;
      }
      if (target.closest('[data-open-picker]')) {
        this.openSheet('picker', target.closest('[data-open-picker]'));
        return;
      }
      if (target.closest('[data-open-save]')) {
        this.openSheet('save', target.closest('[data-open-save]'));
        return;
      }
      if (target.closest('[data-sheet-veil]')) {
        this.closeSheet();
        return;
      }
      const close = target.closest('[data-sheet-close]');
      if (close) {
        const dismissDrawer = close.hasAttribute('data-dismiss-drawer');
        this.closeSheet({ restoreFocus: !dismissDrawer });
        if (dismissDrawer && window.bootstrap?.Offcanvas) window.bootstrap.Offcanvas.getOrCreateInstance(this.drawer).hide();
        return;
      }
      if (target.closest('[data-route-cancel]')) {
        this.cancelRoute();
        return;
      }
      const pickerItem = target.closest('[data-picker-product]');
      if (pickerItem) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (typeof event.button === 'number' && event.button !== 0)) return;
        event.preventDefault();
        this.route(pickerItem);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.drawer.querySelector('[data-route]:not([hidden])')) {
        event.stopImmediatePropagation();
        event.preventDefault();
        this.cancelRoute();
        return;
      }
      if (this.sheet) {
        event.stopImmediatePropagation();
        event.preventDefault();
        this.closeSheet();
      }
    }

    onSubmit(event) {
      const form = event.target.closest('[data-save-form]');
      if (!form) return;
      event.preventDefault();
      this.submitSave(form);
    }

    /* ------------------------------------------------------------
       Sheets
       ------------------------------------------------------------ */
    openSheet(name, opener) {
      const sheet = this.drawer.querySelector(`[data-sheet="${name}"]`);
      if (!sheet) return;
      this.closeSheet({ restoreFocus: false });
      this.sheet = sheet;
      this.opener = opener || document.activeElement;
      const cart = window.getRenderedCartState?.()?.cart;
      if (name === 'picker') this.syncPicker(cart);
      if (name === 'save') {
        this.syncSave(cart);
        this.showSaveStep('form');
      }
      const veil = this.drawer.querySelector('[data-sheet-veil]');
      if (veil) veil.hidden = false;
      sheet.hidden = false;
      sheet.scrollTop = 0;
      this.drawer.classList.add('has-sheet');
      this.setBackgroundInert(true);
      const focusTarget = sheet.querySelector('input:not([type="hidden"])') || sheet.querySelector('a[href], button') || sheet;
      window.requestAnimationFrame(() => focusTarget.focus({ preventScroll: true }));
    }

    closeSheet({ restoreFocus = true } = {}) {
      const sheet = this.sheet;
      if (!sheet) return;
      sheet.hidden = true;
      const veil = this.drawer.querySelector('[data-sheet-veil]');
      if (veil) veil.hidden = true;
      this.drawer.classList.remove('has-sheet');
      this.setBackgroundInert(false);
      const opener = this.opener;
      this.sheet = null;
      this.opener = null;
      if (!restoreFocus) return;
      const target = opener?.isConnected && !opener.closest('[hidden]') ? opener : this.drawer.querySelector('.btn-close');
      target?.focus({ preventScroll: true });
    }

    setBackgroundInert(inert) {
      this.drawer.querySelectorAll('.offcanvas-header, .offcanvas-body, .offcanvas-footer').forEach((region) => {
        if (inert) region.setAttribute('inert', '');
        else region.removeAttribute('inert');
      });
    }

    /* ------------------------------------------------------------
       Free-item picker
       ------------------------------------------------------------ */
    eligibleLines(cart) {
      if (!Array.isArray(cart?.items)) return [];
      const excluded = window.UPSELL_VARIANT_IDS || [];
      return cart.items.filter((item) => item && Number.isInteger(item.quantity) && item.quantity > 0 &&
        !window.isPupRingGiftCardItem?.(item) && !excluded.includes(Number(item.variant_id)));
    }

    freeUnits(lines) {
      return lines.reduce((count, item) => {
        const bogo = (item.line_level_discount_allocations || []).filter(isBogoAllocation).reduce((sum, allocation) => sum + allocation.amount, 0);
        return count + (bogo > 0 && item.original_price > 0 ? Math.floor(bogo / item.original_price) : 0);
      }, 0);
    }

    syncPicker(cart) {
      const sheet = this.drawer.querySelector('[data-sheet="picker"]');
      const list = sheet?.querySelector('[data-picker-list]');
      if (!list) return;
      const items = Array.from(list.querySelectorAll('[data-picker-product]'));
      items.forEach((item, index) => { if (!item.dataset.pickerIndex) item.dataset.pickerIndex = String(index); });
      const lines = this.eligibleLines(cart);
      const units = lines.reduce((count, item) => count + item.quantity, 0);
      // Shopify lists the newest line first; route from the most recently added eligible item.
      const reference = lines[0];
      const referencePrice = reference ? Number(reference.original_price) : null;
      const cartTypes = new Set(lines.map((item) => String(item.product_type || '').trim().toLowerCase()).filter(Boolean));
      const ordered = units === 0
        ? items.slice().sort((a, b) => Number(a.dataset.pickerIndex) - Number(b.dataset.pickerIndex))
        : items.slice().sort((a, b) => {
          const typeA = cartTypes.has(String(a.dataset.pickerType || '').trim().toLowerCase()) ? 0 : 1;
          const typeB = cartTypes.has(String(b.dataset.pickerType || '').trim().toLowerCase()) ? 0 : 1;
          if (typeA !== typeB) return typeA - typeB;
          const distanceA = Math.abs(Number(a.dataset.pickerPrice) - referencePrice);
          const distanceB = Math.abs(Number(b.dataset.pickerPrice) - referencePrice);
          if (distanceA !== distanceB) return distanceA - distanceB;
          return Number(a.dataset.pickerIndex) - Number(b.dataset.pickerIndex);
        });
      ordered.forEach((item) => {
        const freeBlock = item.querySelector('[data-picker-free]');
        const paidBlock = item.querySelector('[data-picker-paid]');
        // Shopify applies BOGO to the cheapest entitled units across the whole cart.
        // A picker candidate cannot safely promise which line will receive the discount.
        if (freeBlock) freeBlock.hidden = true;
        if (paidBlock) paidBlock.hidden = false;
        list.appendChild(item);
      });
      const title = sheet.querySelector('[data-picker-title]');
      if (title) title.textContent = title.dataset.titleNext;
      const state = sheet.querySelector('[data-picker-state]');
      if (state) state.textContent = state.dataset.stateNext;
    }

    route(item) {
      const url = item.dataset.pickerUrl || item.querySelector('a[href]')?.getAttribute('href');
      if (!url) return;
      const overlay = this.drawer.querySelector('[data-route]');
      if (!overlay) {
        window.location.assign(url);
        return;
      }
      const image = overlay.querySelector('[data-route-img]');
      if (image) {
        image.src = item.dataset.pickerImage || '';
        image.hidden = !item.dataset.pickerImage;
      }
      const name = overlay.querySelector('[data-route-name]');
      if (name) name.textContent = item.dataset.pickerName || '';
      const path = overlay.querySelector('[data-route-url]');
      if (path) path.textContent = url;
      overlay.hidden = false;
      overlay.querySelector('[data-route-cancel]')?.focus({ preventScroll: true });
      clearTimeout(this.routeTimer);
      this.routeTimer = setTimeout(() => window.location.assign(url), ROUTE_DELAY_MS);
    }

    cancelRoute() {
      clearTimeout(this.routeTimer);
      this.routeTimer = null;
      const overlay = this.drawer.querySelector('[data-route]');
      if (!overlay || overlay.hidden) return;
      overlay.hidden = true;
      (this.sheet?.querySelector('a[href], button') || this.drawer.querySelector('.btn-close'))?.focus({ preventScroll: true });
    }

    /* ------------------------------------------------------------
       Save cart for later (Klaviyo "Saved Cart")
       ------------------------------------------------------------ */
    scheduleSavePrompt() {
      this.saveTimers.forEach((timer) => clearTimeout(timer));
      this.saveTimers = [SAVE_MIN_AGE_MS + 50, SAVE_DWELL_MS + 50].map((delay) => setTimeout(() => this.updateSavePrompt(), delay));
    }

    marketingAllowed() {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy || typeof privacy.marketingAllowed !== 'function') return false;
      try { return privacy.marketingAllowed() === true; } catch (error) { return false; }
    }

    updateSavePrompt() {
      const prompt = this.drawer.querySelector('[data-save-prompt]');
      if (!prompt) return;
      const openedAt = Number(storage.get('sessionStorage', OPENED_KEY)) || 0;
      const dwell = openedAt ? Date.now() - openedAt : 0;
      const rewardEarned = this.rewardUnlockedAt > 0 || (this.seen?.unlocked.size || 0) > 0;
      const eligible = dwell >= SAVE_MIN_AGE_MS && (rewardEarned || dwell >= SAVE_DWELL_MS) && this.marketingAllowed();
      prompt.hidden = !eligible;
    }

    showSaveStep(step) {
      const sheet = this.drawer.querySelector('[data-sheet="save"]');
      sheet?.querySelectorAll('[data-save-step]').forEach((region) => { region.hidden = region.dataset.saveStep !== step; });
    }

    syncSave(cart) {
      const sheet = this.drawer.querySelector('[data-sheet="save"]');
      if (!sheet || !cart) return;
      const lines = this.eligibleLines(cart);
      const units = lines.reduce((count, item) => count + item.quantity, 0);
      const free = this.freeUnits(lines);
      const currency = cart.currency || window.Shopify?.currency?.active || 'USD';
      const total = window.formatShopMoney(cart.total_price || 0, currency);
      const rewardHeld = free > 0;
      const copy = sheet.querySelector('[data-save-copy]');
      if (copy) copy.textContent = rewardHeld ? copy.dataset.copyReward : copy.dataset.copyDefault;
      const sentCopy = sheet.querySelector('[data-sent-copy]');
      if (sentCopy) sentCopy.textContent = rewardHeld ? sentCopy.dataset.copyReward : sentCopy.dataset.copyDefault;
      const thumbs = sheet.querySelector('[data-save-thumbs]');
      if (thumbs) {
        thumbs.textContent = '';
        cart.items.slice(0, 3).forEach((item) => {
          if (!item.image) return;
          const img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.width = 40;
          img.height = 40;
          img.loading = 'lazy';
          thumbs.appendChild(img);
        });
      }
      const summary = sheet.querySelector('[data-save-summary]');
      if (summary) {
        const parts = [units === 1 ? summary.dataset.summaryOne : fill(summary.dataset.summaryOther, { count: units })];
        if (free > 0) parts.push(fill(summary.dataset.summaryFree, { count: free }));
        parts.push(total);
        summary.textContent = parts.join(' · ');
      }
      const checkout = sheet.querySelector('[data-save-checkout]');
      if (checkout) checkout.textContent = fill(checkout.dataset.labelTemplate, { total });
    }

    buildSavedCartPayload(cart, token) {
      const lines = this.eligibleLines(cart);
      const currency = cart.currency || 'USD';
      // Shopify's cart/c/{token} link rebuilds this exact cart, line-item properties included, on any device.
      const restoreUrl = token ? `${window.location.origin}/cart/c/${token}` : `${window.location.origin}/cart`;
      return {
        $value: Math.round(cart.total_price) / 100,
        Currency: currency,
        ItemCount: cart.item_count,
        FreeItems: this.freeUnits(lines),
        RestoreUrl: restoreUrl,
        PreviewImageUrl: cart.items.find((item) => item.image)?.image || '',
        Items: cart.items.map((item) => ({
          ProductTitle: item.product_title,
          VariantTitle: item.variant_title,
          Quantity: item.quantity,
          LinePrice: Math.round(item.final_line_price) / 100,
          ImageUrl: item.image || ''
        }))
      };
    }

    // The Liquid snapshot carries no cart token; confirm the live cart still matches before sending its link.
    cartIdentity(cart) {
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || !Number.isInteger(cart.total_price)) return null;
      const items = cart.items.map((item) => ({
        key: String(item?.key || ''),
        variantId: String(item?.variant_id || item?.id || ''),
        productId: String(item?.product_id || ''),
        quantity: Number(item?.quantity),
        finalLinePrice: Number(item?.final_line_price)
      })).sort((a, b) => a.key.localeCompare(b.key));
      if (items.some((item) => !item.key || !item.variantId || !Number.isInteger(item.quantity) || item.quantity <= 0 ||
        !Number.isInteger(item.finalLinePrice) || item.finalLinePrice < 0)) return null;
      return JSON.stringify({ currency: String(cart.currency || ''), itemCount: cart.item_count, totalPrice: cart.total_price, items });
    }

    async readCartToken(cart) {
      const response = await fetch('/cart.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart read failed');
      const live = await response.json();
      const renderedIdentity = this.cartIdentity(cart);
      const liveIdentity = this.cartIdentity(live);
      if (!live || typeof live.token !== 'string' || !renderedIdentity || liveIdentity !== renderedIdentity) throw new Error('cart changed');
      return { token: live.token, cart: live };
    }

    trackSavedCart(email, payload) {
      const modern = window.klaviyo;
      if (modern && typeof modern.push === 'function') {
        modern.push(['identify', { $email: email }]);
        modern.push(['track', 'Saved Cart', payload]);
        return true;
      }
      const legacy = window._learnq;
      if (legacy && typeof legacy.push === 'function') {
        legacy.push(['identify', { $email: email }]);
        legacy.push(['track', 'Saved Cart', payload]);
        return true;
      }
      return false;
    }

    async submitSave(form) {
      const input = form.querySelector('input[type="email"]');
      const error = form.querySelector('[data-save-error]');
      const button = form.querySelector('[data-save-submit]');
      const email = (input?.value || '').trim();
      const fail = (message) => {
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        input?.focus({ preventScroll: true });
      };
      if (error) error.hidden = true;
      if (!EMAIL_PATTERN.test(email)) {
        fail(error?.dataset.errorEmail || 'Enter a valid email address.');
        return;
      }
      if (!this.marketingAllowed()) {
        fail(error?.dataset.errorFailed || 'We could not save your cart. Please try again.');
        return;
      }
      const state = window.getRenderedCartState?.();
      const cart = state?.cart;
      if (!window.isCartRefreshResultCurrent?.(state) || !cart || !cart.item_count || form.dataset.saving === 'true') {
        fail(error?.dataset.errorFailed || 'We could not save your cart. Please try again.');
        return;
      }
      form.dataset.saving = 'true';
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      let tracked = false;
      try {
        const live = await this.readCartToken(cart);
        const current = window.getRenderedCartState?.();
        if (this.marketingAllowed() && window.isCartRefreshResultCurrent?.(current) && current.cart === cart) {
          tracked = this.trackSavedCart(email, this.buildSavedCartPayload(live.cart, live.token));
        }
      } catch (exception) {
        tracked = false;
      } finally {
        form.dataset.saving = 'false';
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
      if (!tracked) {
        fail(error?.dataset.errorFailed || 'We could not save your cart. Please try again.');
        return;
      }
      this.showSaveStep('sent');
      this.drawer.querySelector('[data-sheet="save"] [data-save-checkout]')?.focus({ preventScroll: true });
    }
  }

  const start = () => CartDrawerDesign.getInstance().init();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.('#offcanvas-cart')) start();
  });
  window.CartDrawerDesign = CartDrawerDesign;
})();
